import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Mail, Reply, User, Briefcase, Calendar, Hash } from 'lucide-react';
import { PreviewTipJarInviteOutputType } from 'zite-endpoints-sdk';

type PreviewData = PreviewTipJarInviteOutputType;

interface Props {
  open: boolean;
  onClose: () => void;
  data: PreviewData | null;
}

function MetaRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  return (
    <div className="flex items-start gap-2.5 text-sm">
      <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
      <span className="text-muted-foreground w-16 shrink-0">{label}</span>
      <span className="text-foreground font-medium break-all">{value}</span>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-muted-foreground shrink-0 w-40">{label}</span>
      <span className="text-foreground">{value}</span>
    </div>
  );
}

export default function TipJarEmailPreview({ open, onClose, data }: Props) {
  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-primary" />
            Email Preview
          </DialogTitle>
        </DialogHeader>

        {/* Email metadata */}
        <div className="bg-muted/30 border border-border rounded-lg p-4 space-y-2.5">
          <MetaRow icon={Mail} label="To" value={data.toEmail} />
          <MetaRow icon={Reply} label="Reply-To" value={`${data.replyToName} <${data.replyToEmail}>`} />
          <div className="flex items-start gap-2.5 text-sm">
            <Mail className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />
            <span className="text-muted-foreground w-16 shrink-0">Subject</span>
            <span className="text-foreground font-semibold">{data.subject}</span>
          </div>
        </div>

        {/* Email body mock */}
        <div className="border border-border rounded-lg overflow-hidden">
          {/* Header */}
          <div className="bg-card px-6 py-4 border-b border-border flex items-center gap-3">
            <img
              src="https://staffhub.wcdns.co.uk/rattlebone-logo.png"
              alt="Rattlebone"
              className="w-10 h-10 rounded object-cover"
            />
            <span className="font-semibold text-sm text-foreground">The Rattlebone Staff Hub</span>
          </div>

          {/* Body */}
          <div className="bg-background px-6 py-5 space-y-4">
            <p className="text-sm text-foreground leading-relaxed">
              Hi Emily,
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Please find below the details of a new staff member who requires TipJar enrolment.
              Kindly send them an invitation at your earliest convenience.
            </p>
            <p className="text-xs text-muted-foreground italic">
              Sent by {data.replyToName} — reply to this email to contact them directly.
            </p>

            <Separator />

            {/* Employee details */}
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <User className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Employee Details</span>
              </div>
              <div className="space-y-1.5 pl-1">
                <Field label="Employee Name" value={data.employeeName} />
                <Field label="Email Address" value={data.employeeEmail} />
                <Field label="Position" value={data.position} />
                <Field label="Department" value={data.department} />
                <Field label="Employment Type" value={data.employmentType} />
                <Field label="Start Date" value={data.startDate} />
                <Field label="TipJar Reference" value={data.tipJarReference} />
              </div>
            </div>

            <Separator />

            {/* Payroll status */}
            <div>
              <div className="flex items-center gap-1.5 mb-3">
                <Briefcase className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Payroll System Status</span>
              </div>
              <div className="space-y-1.5 pl-1">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground shrink-0 w-40">Planday (Scheduling)</span>
                  <Badge variant={data.plandayId ? 'default' : 'secondary'} className="text-xs">
                    {data.plandayId ? `✅ Set up — ${data.plandayId}` : '❌ Not yet set up'}
                  </Badge>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground shrink-0 w-40">Xero (Payroll)</span>
                  <Badge variant={data.xeroId ? 'default' : 'secondary'} className="text-xs">
                    {data.xeroId ? `✅ Set up — ${data.xeroId}` : '❌ Not yet set up'}
                  </Badge>
                </div>
              </div>
            </div>

            <Separator />

            {/* Footer */}
            <p className="text-xs text-muted-foreground leading-relaxed">
              This request was sent by <strong>{data.senderName}</strong> ({data.senderEmail}) from The Rattlebone Staff Hub on {data.sentDate}.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
